// pf/panel.ts — the "Provably fair" door with an RGS driver installed (SC-61813). The vendored prototype's own panel
// (seeds generated in the window, a seed rotation, a "next hash", a client-seed input and its HMAC recipe) describes a
// model the RGS does not run: no REAL roll can be reproduced from it, and a player following it sees nothing match. The
// seam (setFairView) hands the door to this view instead: the RGS's OWN data for each round of the session and the RGS
// derivation, with a Verify per round that recomputes it in the browser.
//
// Honest about what is checkable: each round's server seed is FRESH and revealed with its result, together with its
// SHA-256 commitment; recomputing from the revealed seeds must give the roll shown. The credited AMOUNT is the server's
// (pf/verify.ts): the door states it next to what the payout formula gives and claims no more. The panel does not claim
// the commitment was shown before the bet (the RGS returns both with the result).
// Every server-supplied string is set as text (never parsed as markup).
//
// ON PF TICKETS (SC-65843, pf-ticket-v1; design note §3.7, product's five values): the player holds each round's
// commitment BEFORE choosing anything. The door then shows, before the next bet, the commitment of the server seed that
// bet will be played on, the settlement terms it will be paid under, and the client seed it will use (editable,
// single-use); and each ticket round is checked by the CONTRACT's verifier against what the device held — the roll AND
// the paid amount (pf/ticket-verify.ts). No rotate button: every round has its own seed, revealed when it ends. While the
// server has not put Dice on tickets ("legacy"), the door says so and keeps the check above for every round.
import type { PfBindingResult, PfRoundEvidence, PfTicketPayload } from "@blocklabsdev/rgs-game-manager-frontend-api";
import type { RoundRecord } from "../operator";
import type { TicketCheck } from "./ticket-verify";
import type { PfTicketTracker } from "./tickets";
import type { VerifyReport } from "./verify";

export interface FairPanelDeps {
  /** REAL: the RGS plays every roll. demo (FUN): the in-page platform does, with seeds made in this window (SC-65843). */
  readonly mode: "real" | "demo";
  readonly records: ReadonlyMap<number, RoundRecord>;
  readonly verify: (ref: number) => Promise<VerifyReport | null>;
  /** the game's integer minor units (a round's stake and payout) → the player's currency text */
  readonly formatMoney: (minor: number) => string;
  /** SC-68493: the wire's integer minor units (a ticket's settlement terms) → the text, with every digit they have. Absent:
   *  formatMoney (a two-decimal currency, where the two units are one). */
  readonly formatWireMoney?: (minor: number) => string;
  /** The pf ticket state and proofs (SC-65843); absent for a game the SDK put on no tickets (the FUN demo). */
  readonly tickets?: PfTicketTracker;
  /** The contract's check of a ticket round (pf/ticket-verify.ts with the SDK's binding check). */
  readonly checkTicket?: (evidence: PfRoundEvidence, binding?: PfBindingResult) => Promise<TicketCheck>;
}

/** How many rounds the door lists at a time (newest first); "Show older rounds" reaches every kept round. */
export const PANEL_ROUNDS = 40;

const RECIPE =
  "msg   = LE64(len(clientSeed)) ‖ clientSeed ‖ BE64(nonce)        (clientSeed hex-decoded)\n" +
  "block = HMAC-SHA256(serverSeed, BE32(i) ‖ msg),  i = 0, 1, 2, …\n" +
  "  → a stream of big-endian 64-bit values\n" +
  "  → k = a uniform integer in [0, 10000) (values in the biased tail are rejected)\n" +
  "  → roll = k / 100\n" +
  "payout = floor(stake × floor(970000 × 10000 / winning outcomes) / 1,000,000)";

function el<K extends keyof HTMLElementTagNameMap>(doc: Document, tag: K, attrs: Record<string, string> = {}, text?: string) {
  const node = doc.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) node.setAttribute(k, v);
  if (text !== undefined) node.textContent = text;
  return node;
}

function betLabel(rec: RoundRecord): string {
  const t2 = (n: number | undefined) => ((n ?? 0) / 100).toFixed(2);
  const s = rec.sel;
  if (s.mode === "under") return `Under ${t2(s.target)}`;
  if (s.mode === "over") return `Over ${t2(s.target)}`;
  return `${s.mode === "between" ? "Between" : "Outside"} ${t2(s.lo)}–${t2(s.hi)}`;
}

function seedRow(doc: Document, label: string, value: string): HTMLElement {
  const row = el(doc, "div", { class: "cpseed" });
  row.appendChild(el(doc, "span", { class: "cpseed__lbl" }, label));
  const val = el(doc, "div", { class: "cpseed__val" });
  val.appendChild(el(doc, "code", {}, value));
  row.appendChild(val);
  return row;
}

/** verified: proven against a commitment this device held before the bet (green). neutral: nothing contradicts it, but
 *  nothing more is proven — a legacy round (its commitment came WITH the result), a proof not out yet, an order this device
 *  cannot prove, another contract (grey). failed: something contradicts what was committed or recomputed (red). */
export type Tone = "verified" | "neutral" | "failed";
export interface Said {
  readonly text: string;
  readonly tone: Tone;
}
const TONE_COLOUR: Readonly<Record<Tone, string>> = { verified: "var(--win)", neutral: "var(--cps-ink-mid)", failed: "var(--loss)" };
function show(out: HTMLElement, said: Said): void {
  out.textContent = said.text;
  out.setAttribute("data-tone", said.tone);
  out.style.color = TONE_COLOUR[said.tone];
}

/** Said of every legacy round that checks out: what the check cannot prove. */
const NOT_PROVEN = "Consistent, not proven: the commitment came with the result, so the seed could have been chosen after your bet.";

// What a legacy check says. The game can check the ROLL against the seed and the commitment that came WITH it (the reveal
// is well-formed, the seed hashes to that commitment, the roll and its win/lose are the ones shown) — consistent, never
// "verified": a server could pick the seed after the bet and send its matching hash (Codex review, SC-65844). It cannot
// verify the AMOUNT: the server may apply an operator limit, a bonus or another denomination that are not in the data the
// game receives (pf/verify.ts). So an amount equal to what the published formula gives is "payout OK"; any other amount
// is stated next to the formula's — never explained away for THIS round, never called failed.
export function reportText(report: VerifyReport | null, rec: RoundRecord, formatMoney: (minor: number) => string, demo = false): Said {
  if (!report) return { text: "No record of this round in this session.", tone: "failed" };
  const parts = [
    `Roll ${report.recomputed.roll.toFixed(2)}`,
    `commitment ${report.commitmentOk ? "OK" : "FAILED"}`,
    `roll ${report.rollOk ? "OK" : "FAILED"}`,
  ];
  if (!report.ok) return { text: `${parts.join(" · ")} — NOT verified.`, tone: "failed" };
  if (report.payoutOk) return { text: `${parts.join(" · ")} · payout OK. ${NOT_PROVEN}`, tone: "neutral" };
  return {
    text:
      `${parts.join(" · ")}. ${NOT_PROVEN} ${demo ? "The demo credited" : "The server credited"} ${formatMoney(rec.expected.payoutMinor)}; the payout formula gives ` +
      `${formatMoney(report.recomputed.payoutMinor)} for this roll. The game cannot verify the credited amount from the data it receives.`,
    tone: "neutral",
  };
}

// ---- pf tickets (SC-65843) -----------------------------------------------------------------------------------------

/** FUN: the in-page platform plays every roll with seeds made in this window. Nothing here is a server's (design note
 *  §2.4: the FUN demo never reaches the RGS, so its panel must not claim provable fairness). */
export const DEMO_INTRO =
  "This is a demo: every roll is played in this window, with seeds made here, not by the RGS server. Recomputing a roll " +
  "below shows how the check works; it proves nothing about real play.";
/** REAL, the server has not put Dice on pf tickets: the commitment arrives with the result. */
export const LEGACY_INTRO =
  "Every roll is decided by the RGS server with a fresh server seed. The seed is revealed with the result, " +
  "together with its SHA-256 commitment: anyone can check that the seed matches the commitment and recompute " +
  "the roll from it. Because the commitment arrives with the result, this does not prove the seed was chosen before your bet.";
const TICKET_INTRO =
  "Every roll is played on a server seed whose SHA-256 commitment you hold before you bet. When the roll ends the " +
  "seed is revealed: check that it matches the commitment you held, then recompute the roll and the amount paid.";

/** What one ticket check says, in the door's words. `ok` colours it; only a verified round (and a revealed refused bet)
 *  is green. A round is never called verified when this device did not hold its commitment before the bet. */
export function ticketText(check: TicketCheck, formatMoney: (minor: number) => string, credited?: number): Said {
  switch (check.kind) {
    case "verified":
      if (check.amount === "verified") {
        return { text: `Commitment held before your bet · roll · amount paid${credited !== undefined ? ` ${formatMoney(credited)}` : ""} — Verified.`, tone: "verified" };
      }
      if (check.amount === "not_applicable") return { text: "Commitment held before your bet · roll — Roll verified. The round was cancelled and the stake refunded.", tone: "verified" };
      return check.unverifiedBecause === "more_than_owed"
        ? {
            text: "Commitment held before your bet · roll — Roll verified. The amount paid is more than the game pays for this roll (a bonus or a promotion), which the published contract does not cover.",
            tone: "verified",
          }
        : {
            text: "Commitment held before your bet · roll — Roll verified. The amount paid cannot be verified: no settlement terms this game can check were held before the bet.",
            tone: "verified",
          };
    case "mismatch":
      return { text: `NOT verified: ${check.failed.join(", ").replace(/_/g, " ") || "the round"} does not match what was held before the bet.`, tone: "failed" };
    case "proof_outstanding":
      return { text: "The server has not revealed this round's seed yet. Check again later.", tone: "neutral" };
    case "ordering_evidence_lost":
      return {
        text: "The seed matches, but this device did not hold the commitment before the bet, so the order cannot be proven from here. Not verified.",
        tone: "neutral",
      };
    case "other_contract":
      return { text: "This round was played under a contract this version of the game cannot check.", tone: "neutral" };
    case "retired":
      return check.consistent
        ? { text: "Not placed: nothing was taken. Its seed was revealed and matches the commitment held before the bet.", tone: "verified" }
        : { text: "Not placed: nothing was taken. Its revealed seed does NOT match the commitment held before the bet.", tone: "failed" };
  }
}

/** The settlement terms of the next bet, in one line: the ceiling and what it does, and the unit the amount is paid in. */
function termsText(t: NonNullable<Extract<PfTicketPayload, { mode: "tickets" }>["ticket"]["acceptedTerms"]>, formatMoney: (minor: number) => string): string {
  const credit = Number(t.denominationMinor);
  const ceiling = t.maxWinMinor !== undefined ? formatMoney(Number(t.maxWinMinor)) : "";
  const cap =
    t.maxWinSemantics === "clamp"
      ? `A win is paid up to ${ceiling}.`
      : t.maxWinSemantics === "refuse"
        ? `A bet that could win more than ${ceiling} is refused.`
        : "No maximum win.";
  // The amount is paid in the currency's smallest unit; a win's fraction of that unit is paid or not by the round's
  // rounding draw (stochastic), or dropped (floor). The denomination is what a stake counts in, not the payment's unit.
  const smallest = formatMoney(1);
  const settle =
    t.settlementPolicy === "stochastic"
      ? `A win's fraction of ${smallest} is paid or not by a draw from the round's own seed.`
      : `A win's fraction of ${smallest} is not paid.`;
  const stake = credit !== 1 ? ` Stakes count in credits of ${formatMoney(credit)}.` : "";
  return `${cap} ${settle}${stake}`;
}

/** What the last "Use this seed" answered, kept across the block's re-renders while the door is open (the SDK publishes
 *  the new ticket state before its promise resolves, which re-renders the block). */
type SeedStatus = Said;

/** The "next roll" block: what the player holds BEFORE the bet (or why there is nothing to hold). */
function renderTicketState(doc: Document, host: HTMLElement, deps: FairPanelDeps, state: PfTicketPayload | null, seed: { status: SeedStatus | null }): void {
  host.replaceChildren();
  if (state === null) return; // no tickets at all (the FUN demo): the legacy intro says everything
  if (state.mode === "legacy") {
    host.appendChild(
      el(doc, "p", { "data-pf": "legacy", style: "font-size:12px" }, "This game is not on provably-fair tickets yet: a round's commitment reaches you with its result, not before the bet."),
    );
    return;
  }
  if (state.mode === "unavailable") {
    host.appendChild(el(doc, "p", { "data-pf": "unavailable", style: "font-size:12px" }, "A provably-fair ticket cannot be held right now. Your next bet waits for one."));
    return;
  }
  host.appendChild(el(doc, "h3", { style: "margin:0 0 6px;font-size:13px" }, "Your next roll"));
  host.appendChild(seedRow(doc, "Commitment — SHA-256(server seed of your next roll)", state.ticket.commitment));
  const terms = state.ticket.acceptedTerms;
  host.appendChild(
    el(
      doc,
      "p",
      { "data-pf": "terms", style: "font-size:12px;margin:6px 0" },
      terms ? termsText(terms, deps.formatWireMoney ?? deps.formatMoney) : "No settlement terms were stated with this ticket: only the roll will be verifiable, not the amount paid.",
    ),
  );
  const row = el(doc, "div", { class: "cpseed" });
  row.appendChild(el(doc, "label", { class: "cpseed__lbl", for: "pfNextSeed" }, "Client seed of your next roll (yours to change, used once)"));
  const input = el(doc, "input", { id: "pfNextSeed", type: "text", spellcheck: "false", autocomplete: "off", "data-pf": "next-seed", "aria-describedby": "pfNextSeedMsg" });
  input.value = state.nextClientSeed;
  row.appendChild(input);
  host.appendChild(row);
  const apply = el(doc, "button", { class: "cpbtn", type: "button", "data-pf": "use-seed" }, "Use this seed");
  const msg = el(doc, "div", { id: "pfNextSeedMsg", role: "status", "data-pf": "seed-msg", style: "font-size:12px" });
  if (seed.status) show(msg, seed.status);
  const say = (status: SeedStatus): void => {
    seed.status = status;
    // the block may have been re-rendered meanwhile: write on the one that is there now
    const shown = host.querySelector<HTMLElement>('[data-pf="seed-msg"]');
    if (shown) show(shown, status);
  };
  apply.addEventListener("click", () => {
    const value = input.value.trim().toLowerCase();
    if (!deps.tickets) return;
    seed.status = null;
    deps.tickets.setNextClientSeed(value).then(
      (ok) =>
        say(ok ? { text: "Your next roll will use this seed.", tone: "verified" } : { text: "Not used: a client seed is hex, and each one can be used once on this device.", tone: "failed" }),
      () => say({ text: "The seed could not be set.", tone: "failed" }),
    );
  });
  host.appendChild(apply);
  host.appendChild(msg);
}

/** The ticket rounds that are not in the rounds list above — bets refused before any debit, voided rounds, and a settled
 *  round the game did not show (it stopped on a missing reveal; its proof may come later) — each with its proof's check. */
function renderUnplaced(doc: Document, host: HTMLElement, deps: FairPanelDeps): void {
  host.replaceChildren();
  // a proof belongs to a listed round by its round id or by its attempt (a late reveal's proof may not name the round)
  const listed = new Set([...deps.records.values()].map((r) => r.clientRoundId));
  const attempts = new Set([...deps.records.values()].flatMap((r) => (r.pf ? [r.pf.attempt.attemptId] : [])));
  const unplaced = (deps.tickets?.proofs() ?? []).filter(
    (p) => !((p.clientRoundId !== undefined && listed.has(p.clientRoundId)) || attempts.has(p.evidence.attempt.attemptId)),
  );
  if (unplaced.length === 0 || !deps.checkTicket) return;
  host.appendChild(el(doc, "h3", { style: "margin:12px 0 6px;font-size:13px" }, "Other bets: not placed, cancelled or not shown"));
  for (const proof of unplaced) {
    const item = el(doc, "div", { "data-pf": "unplaced", style: "font-size:12px;margin:0 0 8px" });
    item.appendChild(seedRow(doc, "Commitment held before the bet", proof.evidence.ticket.commitment ?? "(not held on this device)"));
    const out = el(doc, "div", { role: "status", "data-pf": "out" }, "Checking…");
    item.appendChild(out);
    host.appendChild(item);
    deps.checkTicket(proof.evidence, proof.binding).then(
      (check) => show(out, ticketText(check, deps.formatMoney)),
      () => show(out, { text: "Could not check this bet.", tone: "failed" }),
    );
  }
}

/** The live subscription of the door currently rendered: one door is open at a time, so a new render ends the last one. */
let detachDoor: (() => void) | undefined;

/** Renders the door into the sheet body (which the vendor emptied). */
export function renderFairPanel(body: HTMLElement, given: FairPanelDeps): void {
  detachDoor?.();
  detachDoor = undefined;
  const doc = body.ownerDocument;
  // the demo's platform is the page itself: whatever a tracker says, it holds no ticket from a server
  const { tickets: _demoHasNone, ...demo } = given;
  const deps: FairPanelDeps = given.mode === "demo" ? demo : given;
  const onTickets = deps.tickets?.state()?.mode === "tickets";
  const intro = deps.mode === "demo" ? DEMO_INTRO : onTickets ? TICKET_INTRO : LEGACY_INTRO;
  body.appendChild(el(doc, "p", { style: "margin:0 0 14px;color:var(--cps-ink-dim);font-size:12px", "data-pf": "intro" }, intro));
  const ticketHost = el(doc, "div", { "data-pf": "next" });
  body.appendChild(ticketHost);
  const seed: { status: SeedStatus | null } = { status: null };
  renderTicketState(doc, ticketHost, deps, deps.tickets?.state() ?? null, seed);
  const unplacedHost = el(doc, "div", { "data-pf": "unplaced-list" });
  if (deps.tickets) {
    // Live while the door is open: a new ticket after each roll, a proof that arrives. The listener leaves with the door.
    const off = deps.tickets.onChange(() => {
      if (!ticketHost.isConnected) return void off();
      // the server's answer on tickets arrived while the door was open (the SDK asks once per page): the whole door
      // changes its words — the intro and the how-to — not only the next-roll block. Only a definite answer changes them:
      // "unavailable" is also the moment between a spent ticket and its successor (the SDK clears one before the next
      // arrives), and re-drawing then would close the round the player is reading.
      const mode = deps.tickets!.state()?.mode;
      const nowOnTickets = mode === "tickets" ? true : mode === "legacy" ? false : onTickets;
      if (nowOnTickets !== onTickets) {
        body.replaceChildren();
        return void renderFairPanel(body, deps);
      }
      renderTicketState(doc, ticketHost, deps, deps.tickets!.state(), seed);
      renderUnplaced(doc, unplacedHost, deps);
    });
    detachDoor = () => void off();
  }

  const all = [...deps.records.values()].sort((a, b) => b.ref - a.ref);
  if (all.length === 0) {
    body.appendChild(el(doc, "p", { "data-pf": "empty" }, "No rolls yet this session."));
  } else {
    const list = el(doc, "div", { class: "rowlist", "data-pf": "rounds" });
    const appendRound = (rec: RoundRecord): void => {
      const won = rec.expected.result === "win";
      const row = el(doc, "div", { class: "rl", "data-w": won ? "1" : "0", "data-ref": String(rec.ref) });
      row.appendChild(el(doc, "span", { class: "n" }, `#${rec.ref}`));
      row.appendChild(el(doc, "span", { class: "b" }, betLabel(rec)));
      row.appendChild(el(doc, "span", { class: "r" }, (rec.expected.k / 100).toFixed(2)));
      const net = rec.expected.payoutMinor - rec.betMinor;
      row.appendChild(el(doc, "span", { class: "p" }, `${net >= 0 ? "+" : "−"}${deps.formatMoney(Math.abs(net))}`));
      list.appendChild(row);

      const details = el(doc, "details", { class: "pfMore", "data-pf": "round", "data-ref": String(rec.ref) });
      details.appendChild(el(doc, "summary", {}, `Round #${rec.ref} — seeds and check`));
      const inner = el(doc, "div", { class: "pfBody" });
      const ticket = rec.pf;
      if (ticket && deps.checkTicket) {
        // A ticket round: the commitment the device HELD before the bet, the reveal, and the contract's check — of the
        // NEWEST proof of this attempt (a reveal that came after the round settled replaces the evidence it was recorded with).
        const latest = (): { evidence: PfRoundEvidence; binding?: PfBindingResult } => {
          const proof = deps.tickets?.proofForAttempt(ticket.attempt.attemptId);
          return proof ? { evidence: proof.evidence, binding: proof.binding } : { evidence: ticket };
        };
        const seeds = el(doc, "div", { "data-pf": "seeds" });
        const paintSeeds = (ev: PfRoundEvidence): void => {
          seeds.replaceChildren();
          seeds.appendChild(seedRow(doc, "Commitment held before your bet — SHA-256(server seed)", ev.ticket.commitment ?? "(not held on this device)"));
          seeds.appendChild(seedRow(doc, "Server seed (revealed)", ev.reveal?.serverSeed ?? "(not revealed yet)"));
          seeds.appendChild(seedRow(doc, "Client seed", ev.attempt.clientSeed));
          seeds.appendChild(seedRow(doc, "Nonce", String(ev.reveal?.nonce ?? 0)));
        };
        paintSeeds(latest().evidence);
        inner.appendChild(seeds);
        const check = el(doc, "button", { class: "cpbtn cpbtn--primary", type: "button", "data-pf": "verify" }, "Verify this roll");
        const said = el(doc, "div", { "data-pf": "out", role: "status", style: "font-size:12px;line-height:1.5" });
        check.addEventListener("click", () => {
          said.textContent = "Verifying…";
          const now = latest();
          paintSeeds(now.evidence);
          deps.checkTicket!(now.evidence, now.binding).then(
            (c) => show(said, ticketText(c, deps.formatMoney, rec.expected.payoutMinor)),
            () => show(said, { text: "Could not verify this round.", tone: "failed" }),
          );
        });
        inner.appendChild(check);
        inner.appendChild(said);
        details.appendChild(inner);
        list.appendChild(details);
        return;
      }
      inner.appendChild(seedRow(doc, "Commitment — SHA-256(server seed)", rec.fairness.commitment));
      inner.appendChild(seedRow(doc, "Server seed (revealed)", rec.fairness.serverSeed));
      inner.appendChild(seedRow(doc, "Client seed", rec.fairness.clientSeed));
      inner.appendChild(seedRow(doc, "Nonce", String(rec.fairness.revealedNonce)));
      const button = el(doc, "button", { class: "cpbtn cpbtn--primary", type: "button", "data-pf": "verify" }, "Verify this roll");
      const out = el(doc, "div", { "data-pf": "out", role: "status", style: "font-size:12px;line-height:1.5" });
      button.addEventListener("click", () => {
        out.textContent = "Verifying…";
        deps.verify(rec.ref).then(
          (report) => show(out, reportText(report, rec, deps.formatMoney, deps.mode === "demo")),
          () => show(out, { text: "Could not verify this round.", tone: "failed" }),
        );
      });
      inner.appendChild(button);
      inner.appendChild(out);
      details.appendChild(inner);
      list.appendChild(details);
    };
    let shown = 0;
    const more = el(doc, "button", { class: "cpbtn", type: "button", "data-pf": "older" }, "Show older rounds");
    const showPage = (): void => {
      for (const rec of all.slice(shown, shown + PANEL_ROUNDS)) appendRound(rec);
      shown = Math.min(all.length, shown + PANEL_ROUNDS);
      more.hidden = shown >= all.length;
    };
    more.addEventListener("click", showPage);
    showPage();
    body.appendChild(list);
    body.appendChild(more);
  }

  body.appendChild(unplacedHost);
  renderUnplaced(doc, unplacedHost, deps);

  const how = el(doc, "details", { class: "pfMore", "data-pf": "how" });
  how.appendChild(el(doc, "summary", {}, "How the roll is computed, and how to check it"));
  const howBody = el(doc, "div", { class: "pfBody" });
  howBody.appendChild(el(doc, "p", {}, "From the revealed server seed, the client seed and the nonce of the round:"));
  howBody.appendChild(el(doc, "code", { class: "blk" }, RECIPE));
  howBody.appendChild(
    el(
      doc,
      "p",
      {},
      "Check SHA-256(server seed) against the commitment, then recompute: the roll must be the one shown. " +
        "The payout line is the game's formula. " +
        // the demo's amounts are the page's own: no server sets them (SC-65843)
        (deps.mode === "demo"
          ? ""
          : "The amount credited is set by the server, which can apply a limit or a bonus that the game does not receive, so the game cannot verify it. ") +
        "Under and Over split the 10,000 outcomes with no gap (a tie loses Under and wins Over).",
    ),
  );
  if (onTickets) {
    howBody.appendChild(
      el(
        doc,
        "p",
        { "data-pf": "how-tickets" },
        "On provably-fair tickets (pf-ticket-v1) the nonce is always 0, and the commitment is the one you held before the bet. " +
          "The amount paid is then verifiable too: the win in whole units of the terms shown before the bet, a fraction of a " +
          "unit paid when the round's rounding draw (from the same seeds) falls below it, and never more than the maximum win " +
          "shown. The game checks it with the published contract (@blocklabsdev/rgs-contracts, pf-ticket-v1).",
      ),
    );
  }
  how.appendChild(howBody);
  body.appendChild(how);
}
